/**
 * Public Climate Credentials Registry
 * Route: /registry
 * No authentication required. Shows all published SME credentials.
 */
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Globe, Search, Shield, CheckCircle2, AlertCircle, ExternalLink, TrendingDown } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Status badge ──────────────────────────────────────────────────────────
function StatusBadge({ statusKey }) {
  const CONFIG = {
    assessed: { label: "Assessed",        color: "bg-teal-100 text-teal-700 border-teal-200" },
    verified: { label: "Verified",        color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    published:{ label: "Verified",        color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  };
  const c = CONFIG[statusKey] || CONFIG.assessed;
  return (
    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${c.color}`}>{c.label}</span>
  );
}

// ── Registry card ─────────────────────────────────────────────────────────
function RegistryCard({ record }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-200 hover:shadow-sm transition-all"
      data-testid={`registry-card-${record.registry_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* ID + Status */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-mono font-bold text-teal-600">{record.registry_id}</span>
            <StatusBadge statusKey={record.status_key} />
          </div>

          {/* Org name */}
          <p className="text-base font-bold text-[#0F172A] mb-0.5">{record.org_name}</p>
          <p className="text-xs text-slate-400 mb-3">{record.country} · {record.sector}</p>

          {/* Key metrics row */}
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-[9px] text-slate-400 uppercase tracking-wide">Emissions</p>
              <p className="text-sm font-semibold text-slate-900">{record.emissions_tco2e} <span className="text-xs font-normal text-slate-400">tCO₂e/yr</span></p>
            </div>
            {record.crs_score != null && (
              <div>
                <p className="text-[9px] text-slate-400 uppercase tracking-wide">CRS</p>
                <p className="text-sm font-bold text-teal-700">{Math.round(record.crs_score)}/100 <span className="text-xs font-normal text-slate-400">Band {record.crs_band}</span></p>
              </div>
            )}
            {record.benchmark_label && (
              <div className="flex items-center gap-1">
                <TrendingDown size={10} className="text-emerald-500" />
                <p className="text-[10px] text-emerald-700 font-semibold">{record.benchmark_label}</p>
              </div>
            )}
          </div>

          {/* Date + methodology */}
          <p className="text-[9px] text-slate-400 mt-2">{record.date} · {record.methodology}</p>
        </div>

        {/* CTA */}
        <div className="flex-shrink-0">
          {record.verify_url ? (
            <Link to={`/verify/report/${record.verify_url}`}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-colors"
              data-testid={`view-credentials-${record.registry_id}`}>
              View Credentials <ExternalLink size={10} />
            </Link>
          ) : (
            <Link to={`/verify/${record.registry_id}`}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-xs font-medium rounded-xl hover:bg-slate-50"
              data-testid={`view-credentials-${record.registry_id}`}>
              Verify <ExternalLink size={10} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Registry ─────────────────────────────────────────────────────────
export default function PublicRegistry() {
  const { registryId } = useParams();
  const [records,  setRecords]  = useState([]);
  const [search,   setSearch]   = useState("");
  const [loading,  setLoading]  = useState(true);
  const [total,    setTotal]    = useState(0);

  useEffect(() => {
    loadAll(search);
  }, []);

  const loadAll = async (q = "") => {
    setLoading(true);
    try {
      const qs  = q ? `?search=${encodeURIComponent(q)}` : "";
      const res = await fetch(`${BACKEND_URL}/api/public/registry${qs}`);
      const data = await res.json();
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch {}
    setLoading(false);
  };

  const handleSearch = (val) => {
    setSearch(val);
    loadAll(val);
  };

  // If navigating to a specific registry ID (old /verify/:id route)
  if (registryId) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-md">
          <p className="text-sm font-semibold text-slate-500 mb-2">Registry ID: {registryId}</p>
          <Link to="/registry" className="text-sm text-teal-600 hover:underline">← Back to Registry</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans">
      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <div className="bg-[#0F172A] rounded-lg px-3 py-1.5">
              <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-800">Home</Link>
            <Link to="/login" className="text-slate-500 hover:text-slate-800">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-[#0F172A] py-14 px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-900/40 border border-teal-700/30 rounded-full text-xs font-medium text-teal-400 mb-4">
            <Globe size={12} /> Public Registry
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight mb-3">
                Public Climate Credentials Registry
              </h1>
              <p className="text-slate-400 text-sm max-w-xl leading-relaxed">
                Transparent, standardized climate assessments published for verification and reference.
                Each record is generated using the GHG Protocol Corporate Standard.
              </p>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="text-3xl font-bold text-white">{total}</p>
              <p className="text-xs text-slate-400">record{total !== 1 ? "s" : ""} published</p>
            </div>
          </div>
      </div>

      {/* Content */}
      <div className="px-6 py-10 space-y-6">

        {/* Status Legend */}
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Status Definitions</p>
          <div className="flex flex-wrap gap-5">
            <div className="flex items-start gap-2">
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200 flex-shrink-0 mt-0.5">Assessed</span>
              <p className="text-[10px] text-slate-500">Calculated using standard methodology (GHG Protocol). Self-reported data.</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200 flex-shrink-0 mt-0.5">Verified</span>
              <p className="text-[10px] text-slate-500">Third-party reviewed and independently confirmed. <span className="italic">(Coming soon)</span></p>
            </div>
          </div>
        </div>

        {/* Search + count */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder='Search by organization or ID (e.g. "Gasavant" or "DCI-2026-0001")'
              className="w-full h-10 pl-9 pr-3 border border-slate-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              data-testid="public-registry-search" />
          </div>
          <p className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0">
            {total} record{total !== 1 ? "s" : ""} · Newest first
          </p>
        </div>

        {/* Records */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-32 bg-white border border-slate-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Globe size={32} className="mx-auto mb-3 text-slate-200" />
            <p className="text-sm font-medium text-slate-500">
              {search ? `No records found for "${search}"` : "No records yet"}
            </p>
            {search && (
              <button onClick={() => handleSearch("")}
                className="mt-3 text-xs text-teal-600 hover:underline">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="public-registry-list">
            {records.map(r => <RegistryCard key={r.registry_id} record={r} />)}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-6 px-6 mt-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-[#0F172A] rounded px-2 py-1">
              <img src={LOGO_URL} alt="DeCarbonIQ" className="h-4 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
            <span className="text-xs text-slate-400">Public Climate Credentials Registry</span>
          </div>
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} DeCarbonIQ</p>
        </div>
      </footer>
    </div>
  );
}
